import VueApollo from 'vue-apollo';
import { FETCH_ACCOUNT_DATA } from '@/graphql/account/queries';
import { rolesArray } from '@/lib/jwt';

export interface Location {
  cityname: string;
  stateabbr: string;
  latitude: number;
  longitude: number;
  zipcode: string;
}

export interface AccountData {
  ssoUUID: string;
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  originalPhoto: string;
  croppedPhoto: string;
  location: Location;
  roles: string[];
  hasPremiumHistory: boolean;
  profilePhotoStatus: string;
  createdAt?: string;
}

/* eslint-disable no-unused-vars */
/* eslint-disable no-shadow */
export enum AccountRoles {
  FamilyConcierge = 'com.sittercity.empire:Role.family_concierge',
  Sitter = 'com.sittercity.empire:Role.provider',
  Parent = 'com.sittercity.empire:Role.seeker',
  Premium = 'com.sittercity.empire:Role.premium_seeker',
  Corporate = 'com.sittercity.empire:Role.corporate_seeker',
  FeaturedSitter = 'com.sittercity.empire:Role.featured_provider',
}

export enum AccountTypes {
  FamilyConcierge = 'family_concierge',
  Sitter = 'sitter',
  Parent = 'basic',
  Premium = 'premium',
  Corporate = 'corporate',
  FeaturedSitter = 'featured_sitter',
}

export const RoleMap = {} as Map<AccountRoles, AccountTypes>;
RoleMap[AccountRoles.FamilyConcierge] = AccountTypes.FamilyConcierge;
RoleMap[AccountRoles.Corporate] = AccountTypes.Corporate;
RoleMap[AccountRoles.Premium] = AccountTypes.Premium;
RoleMap[AccountRoles.Parent] = AccountTypes.Parent;
RoleMap[AccountRoles.FeaturedSitter] = AccountTypes.FeaturedSitter;
RoleMap[AccountRoles.Sitter] = AccountTypes.Sitter;
/* eslint-enable no-unused-vars */
/* eslint-enable no-shadow */

export function accountType(roles: Array<AccountRoles>): AccountTypes | null {
  // ensure the correct order of precedence
  const found = [
    AccountRoles.FamilyConcierge,
    AccountRoles.Corporate,
    AccountRoles.Premium,
    AccountRoles.Parent,
    AccountRoles.FeaturedSitter,
    AccountRoles.Sitter,
  ].find((role) => roles.includes(role));

  if (!found) return null;
  return RoleMap[found];
}

export class Account implements AccountData {
  static accountType(): import('vue/types/options').PropValidator<unknown> {
    throw new Error('Method not implemented.');
  }

  ssoUUID!: string;

  id!: string;

  firstName!: string;

  lastName!: string;

  email!: string;

  originalPhoto!: string;

  croppedPhoto!: string;

  location!: Location;

  hasPremiumHistory!: boolean;

  profilePhotoStatus!: string;

  roles!: string[];

  createdAt?: string;

  constructor(data: AccountData) {
    Object.assign(this, data);
  }

  isParent(): boolean {
    const parentRoles = [AccountRoles.Parent, AccountRoles.Premium, AccountRoles.Corporate];
    return !!this.roles.find((r: string) => parentRoles.includes(r as AccountRoles));
  }

  isPremium(): boolean {
    return !!this.roles.find((r: string) => r === (AccountRoles.Premium as string));
  }

  isCorporate(): boolean {
    return !!this.roles.find((r: string) => r === (AccountRoles.Corporate as string));
  }

  isSitter(): boolean {
    return !!this.roles.find((r: string) => r === (AccountRoles.Sitter as string));
  }

  isFeaturedSitter(): boolean {
    return !!this.roles.find((r: string) => r === (AccountRoles.FeaturedSitter as string));
  }

  isFamilyConcierge(): boolean {
    return !!this.roles.find((r: string) => r === (AccountRoles.FamilyConcierge as string));
  }

  isBasic(): boolean {
    return !(this.isPremium() || this.isCorporate());
  }

  accountType(): AccountTypes | null {
    return accountType(this.roles as Array<AccountRoles>);
  }

  accountKind(): string {
    if (this.isSitter()) {
      return 'SITTER';
    }
    if (this.isParent()) {
      return 'PARENT';
    }
    return 'UNKNOWN';
  }
}

export async function fetchAccount(apolloProvider: VueApollo, redirectUnauthedLogin: boolean = true): Promise<Account | null> {
  let accountData;

  try {
    const result = await apolloProvider.defaultClient.query({
      query: FETCH_ACCOUNT_DATA,
    });
    accountData = result.data.viewer;
  } catch (error) {
    console.error('Error fetching account data:', error);
    if (redirectUnauthedLogin) {
      window.location.href = '/login';
    }
    return null;
  }

  if (!accountData) {
    if (redirectUnauthedLogin) {
      window.location.href = '/login';
    }
    return null;
  }

  return new Account({
    ssoUUID: accountData.id,
    id: accountData.id,
    firstName: accountData.firstName,
    lastName: accountData.lastName,
    email: accountData.email,
    originalPhoto: accountData.photoUrl,
    croppedPhoto: accountData.photoUrl,
    location: {
      cityname: accountData.location.cityname,
      stateabbr: accountData.location.stateabbr,
      latitude: parseFloat(accountData.location.geoPoint.y),
      longitude: parseFloat(accountData.location.geoPoint.x),
    },
    hasPremiumHistory: accountData.hasPremiumHistory,
    profilePhotoStatus: accountData.profilePhotoStatus,
    createdAt: accountData.createdAt,
    roles: rolesArray(),
  } as AccountData);
}
